import { useCallback, useEffect, useRef, useState } from "react";
import { searchLocations } from "../utils/geocoding";

const DEBOUNCE_MS = 350;
const MIN_CHARS = 3;

/**
 * Hook that powers the location autocomplete.
 * 
 * Takes query as input (controlled from parent) and manages the search results.
 *
 * Returns:
 *  - suggestions             – array of place objects
 *  - isLoading               – true while a fetch is in-flight
 *  - error                   – string or null
 *  - activeIndex / setActive – highlighted suggestion index (-1 = none)
 *  - select(item)            – pick a suggestion
 *  - isOpen                  – whether the dropdown should show
 *  - setIsOpen
 *  - clear()                 – reset everything
 */
export default function useLocationAutocomplete(query) {
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const abortRef = useRef(null);
  const debounceRef = useRef(null);

  // Search function
  const search = useCallback((text) => {
    // Cancel any in-flight request
    if (abortRef.current) abortRef.current.abort();

    const trimmed = text.trim();
    if (trimmed.length < MIN_CHARS) {
      setSuggestions([]);
      setIsOpen(false);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    const controller = new AbortController();
    abortRef.current = controller;

    searchLocations(trimmed, controller.signal)
      .then((results) => {
        setSuggestions(results);
        setActiveIndex(-1);
        setIsOpen(results.length > 0);
        setIsLoading(false);
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        setError("Could not fetch locations. Please try again.");
        setIsLoading(false);
      });
  }, []);

  // When query changes, debounce the search
  useEffect(() => {
    if (selectedItem && query === selectedItem.displayName) {
      // Don't re-search if this is the selected item's text
      return;
    }

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(query), DEBOUNCE_MS);

    return () => clearTimeout(debounceRef.current);
  }, [query, search, selectedItem]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
      clearTimeout(debounceRef.current);
    };
  }, []);

  const select = useCallback((item) => {
    setSelectedItem(item);
    setSuggestions([]);
    setIsOpen(false);
    setActiveIndex(-1);
  }, []);

  const clear = useCallback(() => {
    setSuggestions([]);
    setIsOpen(false);
    setActiveIndex(-1);
    setSelectedItem(null);
    setError(null);
  }, []);

  return {
    suggestions,
    isLoading,
    error,
    activeIndex,
    setActiveIndex,
    isOpen,
    setIsOpen,
    select,
    selectedItem,
    clear,
  };
}
